import { Grid, Skeleton } from '@mui/material';
import { Field, FieldProps, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroIcon,
	CerebroInput,
	CerebroLabel,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../shared/components';
import { useApi } from '../shared/context';
import {
	QueryKeys,
	useDealQuery,
	useNavigateToNextPage,
	useDealPartnerContactQuery,
	useSalesRepsQuery,
	useTransactionManagersQuery,
} from '../shared/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FormikUtils } from '../shared/utils';
import { SearchResult, UpdateDealDto } from '../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import { getAndUpdateDeal } from './DealIntroduction';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { DataRoomRootUrl } from '../shared/utils/platform';
import { validateDateWithinPlusMinus100Years } from '../shared/utils/date';

export const DealSettings: React.FC = () => {
	const [open, setOpen] = React.useState(false);
	const snackbar = useSnackbar();

	const api = useApi();
	const navigateToNextPage = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();
	const queryClient = useQueryClient();
	const dealQuery = useDealQuery(guid);
	const salesRepQuery = useSalesRepsQuery(dealQuery.data?.salesPerson, {
		enabled: dealQuery.isSuccess,
		retry: false,
	});
	const hasCompanySubscription = dealQuery.data?.hasCompanySubscription ?? false;

	const transactionManagerQuery = useTransactionManagersQuery(dealQuery.data?.transactionManager, {
		enabled: dealQuery.isSuccess,
		retry: false,
	});

	const dealMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const dealPatch: UpdateDealDto = {
				...values,
				dealName: values.dealName,
				salesPerson: values.salesPerson != '-1' ? values.salesPerson : null,
				applicationCompleteOverride: values.applicationCompleteOverride ?? false,
				requestDescription: values.requestDescription ?? '',
				dealPartnerContactGuid: values.dealPartnerContact != '-1' ? values.dealPartnerContact : null,
				isDirectIntroDeal: values.isDirectIntroDeal,
				isRawLead: values.isRawLead,
				isRawLeadVisible: values.isRawLeadVisible,
				sourcePage: 'DealSettings',
			};

			return await getAndUpdateDeal(api, guid, dealPatch);
		},
		{
			onSuccess: deal => {
				queryClient.setQueryData(QueryKeys.Deal.Root(guid), deal);
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Deal Settings</CerebroBreadcrumb>

			<Formik
				enableReinitialize
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(dealMutation.mutate)}
				initialValues={
					{
						dealName: dealQuery.data?.dealName ?? '',
						salesPerson: dealQuery.data?.salesPerson ?? '-1',
						applicationCompleteOverride: dealQuery.data?.applicationCompleteOverride ?? null,
						requestDescription: dealQuery.data?.requestDescription ?? '',
						dealPartner: dealQuery.data?.dealPartner ?? null,
						dealPartnerContact: dealQuery.data?.dealPartnerContact?.guid ?? '-1',
						isLenderFinanceDeal: dealQuery.data?.isLenderFinanceDeal ?? false,
						isFloorplanFinanceDeal: dealQuery.data?.isFloorplanFinanceDeal ?? false,
						isEditingCreditAnalysis: dealQuery.data?.isEditingCreditAnalysis ?? false,
						isDirectIntroDeal: dealQuery.data?.isDirectIntroDeal ?? false,
						financialStartDate: dealQuery.data?.financialStartDate ?? '',
						isRawLead: dealQuery.data?.isRawLead ?? false,
						isRawLeadVisible: dealQuery.data?.isRawLeadVisible ?? false,
					} as DealSettingsFormValues
				}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<CerebroLabel htmlFor="dealName">Deal Name</CerebroLabel>
						{dealQuery.isSuccess ? (
							<Field
								id="dealName"
								name="dealName"
								as={CerebroInput}
								placeholder="Name of the deal..."
								disabled={isSubmitting}
							/>
						) : (
							<Skeleton height={60} />
						)}

						<CerebroLabel htmlFor="salesPerson" sx={{ marginTop: 3 }}>
							Sales Rep
						</CerebroLabel>
						{dealQuery.isSuccess && salesRepQuery.isSuccess ? (
							<Field id="salesPerson" as={CerebroDropdown} name="salesPerson" disabled={isSubmitting}>
								<CerebroDropdownMenuItem value="-1">Unassigned</CerebroDropdownMenuItem>
								{salesRepQuery.data.map(rep => (
									<CerebroDropdownMenuItem key={rep.guid + 'sales'} value={rep.guid}>
										{rep.name}
									</CerebroDropdownMenuItem>
								))}
							</Field>
						) : (
							<Skeleton height={60} />
						)}

						{transactionManagerQuery.isSuccess ? (
							<>
								<CerebroLabel
									sx={{ marginTop: 3 }}
									icon={() => <FontAwesomeIcon size="xs" icon={faEdit} />}
									onIconClick={() =>
										window.open(
											`${DataRoomRootUrl}/${dealQuery.data?.dataRoomGuid}/Settings`,
											'_blank',
											'noopener,noreferrer'
										)
									}
								>
									Transaction Manager
								</CerebroLabel>
								<Field
									id="transactionManager"
									name="transactionManager"
									as={CerebroInput}
									disabled={true}
									value={
										transactionManagerQuery.data.find(
											m => m.guid === dealQuery.data?.transactionManager
										)?.name || 'Unassigned'
									}
									sx={{
										backgroundColor: 'transparent',
										color: 'inherit',
										opacity: 1,
										pointerEvents: 'none',
									}}
								/>
							</>
						) : (
							<Skeleton height={60} />
						)}

						{values.dealPartner && (
							<Grid item xs={12} md={6}>
								<CerebroLabel htmlFor="dealPartnerContact">Deal Partner Contact</CerebroLabel>
								<Field
									id="dealPartnerContact"
									name="dealPartnerContact"
									component={DealPartnerContactCerebroDropDown}
									placeholder="Select Deal Partner Contact"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('dealPartnerContact', errors, touched)}
								/>
							</Grid>
						)}

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item md={6}>
								<CerebroLabel
									icon={() => <FontAwesomeIcon icon={faInfoCircle} />}
									onIconClick={() => setOpen(true)}
								>
									Financials Start Date
								</CerebroLabel>

								{dealQuery.isSuccess ? (
									<>
										<Field
											component={CerebroDatePicker}
											name="financialStartDate"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('financialStartDate', errors, touched)}
										/>

										{FormikUtils.IsInvalid('financialStartDate', errors, touched) && (
											<CerebroText variant="error" sx={{ marginBottom: 4 }}>
												{errors.financialStartDate as string}
											</CerebroText>
										)}
									</>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item md={6}>
								<CerebroLabel>Lender Finance Deal</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="isLenderFinanceDeal"
										as={CerebroYesNo}
										name="isLenderFinanceDeal"
										disabled={isSubmitting}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item md={6}>
								<CerebroLabel>Floorplan Finance Deal</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="isFloorplanFinanceDeal"
										as={CerebroYesNo}
										name="isFloorplanFinanceDeal"
										disabled={isSubmitting}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item md={6}>
								<CerebroLabel
									id="isEditingCreditAnalysis"
									icon={() => <FontAwesomeIcon icon={faInfoCircle} />}
									onIconClick={() => setOpen(true)}
								>
									Editing Credit Analysis
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="isEditingCreditAnalysis"
										as={CerebroYesNo}
										name="isEditingCreditAnalysis"
										disabled={isSubmitting}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						{hasCompanySubscription && (
							<Grid container spacing={3} sx={{ marginTop: 3 }}>
								<Grid item md={6}>
									<CerebroLabel
										id="isDirectIntroDeal"
										icon={() => <FontAwesomeIcon icon={faInfoCircle} />}
										onIconClick={() => setOpen(true)}
									>
										Direct Lender Intro
									</CerebroLabel>
									{dealQuery.isSuccess ? (
										<Field
											id="isDirectIntroDeal"
											as={CerebroYesNo}
											name="isDirectIntroDeal"
											disabled={isSubmitting}
										/>
									) : (
										<Skeleton height={60} />
									)}
								</Grid>
							</Grid>
						)}

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item md={6}>
								<CerebroLabel id="isRawLead">Raw Lead Deal</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field id="isRawLead" as={CerebroYesNo} name="isRawLead" disabled={isSubmitting} />
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						{values.isRawLead && (
							<Grid container spacing={3} sx={{ marginTop: 3 }}>
								<Grid item md={6}>
									<CerebroLabel id="isRawLeadVisible">Raw Lead Deal Visible</CerebroLabel>
									{dealQuery.isSuccess ? (
										<Field
											id="isRawLeadVisible"
											as={CerebroYesNo}
											name="isRawLeadVisible"
											disabled={isSubmitting}
										/>
									) : (
										<Skeleton height={60} />
									)}
								</Grid>
							</Grid>
						)}

						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item md={6}>
								<CerebroLabel
									id="applicationCompleteOverride"
									icon={() => <FontAwesomeIcon icon={faInfoCircle} />}
									onIconClick={() => setOpen(true)}
								>
									Application Complete?
								</CerebroLabel>
								{dealQuery.isSuccess ? (
									<Field
										id="applicationCompleteOverride"
										as={CerebroYesNo}
										name="applicationCompleteOverride"
										disabled={isSubmitting}
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="requestDescription" sx={{ marginTop: 3 }}>
							Request Description
						</CerebroLabel>
						{dealQuery.isSuccess ? (
							<Field
								id="requestDescription"
								name="requestDescription"
								as={CerebroTextarea}
								rows={8}
								placeholder="Narrative description of the funding request."
								disabled={isSubmitting}
							/>
						) : (
							<Skeleton height={60} />
						)}

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save &amp; Continue
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>

			<CerebroDrawer open={open} onClose={() => setOpen(false)}>
				<CerebroIcon sx={{ marginBottom: 2 }}>
					<FontAwesomeIcon icon={faInfoCircle} size="3x" />
				</CerebroIcon>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Financials Start Date
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					This is the date and more importantly the year in which all the financials headings will be based
					on. By default this date is based on the creation of the deal.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Application Complete?
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					This will override the loan application to complete regardless of how much information has been
					provided.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Editing Credit Analysis
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					This setting when set to Yes will show the editing features on the the Credit Analysis report page
					for employee users. Anything hidden from borrowers will be displayed with elements showing they are
					hidden. When set to No, the Credit Analysis report page will show the same for employee and borrower
					users. Anything hidden will not be shown and no editing features will be present for any user
					visiting the page.
				</CerebroText>

				{hasCompanySubscription && (
					<>
						<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
							Direct Lender Intro
						</CerebroLabel>
						<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
							If this is a direct lender intro deal, teasers will not be automatically added to the data
							room and the borrower will not be emailed when the deal launches.
						</CerebroText>
					</>
				)}
			</CerebroDrawer>
		</CerebroPageContainer>
	);
};

interface DealPartnerContactCerebroDropDownProps extends FieldProps<any, DealSettingsFormValues> {
	error: boolean;
}

const DealPartnerContactCerebroDropDown: React.FC<DealPartnerContactCerebroDropDownProps> = ({
	error,
	field,
	form,
	meta,
	children,
}) => {
	const dealPartnerContactQuery = useDealPartnerContactQuery(form.values.dealPartner?.guid ?? '');

	return form.values.dealPartner && dealPartnerContactQuery.isSuccess ? (
		<Field error={error} {...field} as={CerebroDropdown} disabled={form.isSubmitting}>
			{dealPartnerContactQuery.data.map(contact => (
				<CerebroDropdownMenuItem key={contact.guid} value={contact.guid}>
					{contact.fullName}
				</CerebroDropdownMenuItem>
			))}
		</Field>
	) : (
		<Skeleton height={60} />
	);
};

function validateForm(values: DealSettingsFormValues) {
	const errors: Partial<Record<keyof DealSettingsFormValues, string>> = {};

	const dateError = validateDateWithinPlusMinus100Years(values.financialStartDate, 'Financials Start Date');

	if (dateError) {
		errors.financialStartDate = dateError;
	}

	return errors;
}

type DealMutationVariables = FormikUtils.MutateOnSubmitVariables<DealSettingsFormValues>;
interface DealSettingsFormValues {
	dealName: string;
	salesPerson: string;
	applicationCompleteOverride: boolean | null;
	requestDescription: string;
	dealPartner: SearchResult | null;
	dealPartnerContact: string;
	isLenderFinanceDeal: boolean;
	isFloorplanFinanceDeal: boolean;
	isEditingCreditAnalysis: boolean;
	isDirectIntroDeal: boolean;
	financialStartDate: string;
	isRawLead: boolean;
	isRawLeadVisible: boolean;
}
